/**
 * File type detection utilities
 * Extracted from S3FilePreview to reduce bundle size and enable better tree-shaking
 */

/**
 * Get MIME type from file extension
 */
export const getFileTypeFromUrl = (url: string): string => {
  // Check for query params and get the real file extension
  const cleanUrl = url.split('?')[0];
  const extension = cleanUrl.split('.').pop()?.toLowerCase();

  if (!extension) {
    // Try to detect PDFs from the pattern in S3 key
    if (url.includes('-') && url.toLowerCase().includes('pdf')) {
      return 'application/pdf';
    }
    return 'application/octet-stream';
  }

  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'svg':
      return 'image/svg+xml';
    case 'pdf':
      return 'application/pdf';
    case 'mp4':
      return 'video/mp4';
    case 'webm':
      return 'video/webm';
    case 'mov':
      return 'video/quicktime';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
    case 'xlsx':
      return 'application/vnd.ms-excel';
    case 'ppt':
    case 'pptx':
      return 'application/vnd.ms-powerpoint';
    case 'csv':
      return 'text/csv';
    case 'txt':
      return 'text/plain';
    case 'json':
      return 'application/json';
    case 'html':
    case 'htm':
      return 'text/html';
    case 'css':
      return 'text/css';
    case 'js':
      return 'application/javascript';
    case 'zip':
    case 'rar':
      return 'application/zip';
    case 'mp3':
    case 'wav':
    case 'ogg':
      return 'audio/mpeg';
    default:
      return 'application/octet-stream';
  }
};

/**
 * Extract file type from URL query parameters
 */
export const extractFileTypeFromUrl = (url: string): string | null => {
  if (!url) return null;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(url, base);
    const typeParam =
      parsed.searchParams.get('type') ||
      parsed.searchParams.get('fileType') ||
      parsed.searchParams.get('contentType');
    return typeParam ? decodeURIComponent(typeParam) : null;
  } catch (_error) {
    return null;
  }
};

/**
 * Categorize file type into a simple category string
 */
export const categorizeFileType = (fileType: string): string => {
  if (fileType.startsWith('image/')) return 'image';
  if (fileType.startsWith('video/')) return 'video';
  if (fileType.startsWith('audio/')) return 'audio';
  if (fileType === 'application/pdf') return 'pdf';
  if (fileType === 'application/msword' || fileType.includes('wordprocessingml')) return 'document';
  if (fileType === 'application/vnd.ms-excel' || fileType.includes('spreadsheetml')) return 'spreadsheet';
  if (fileType === 'application/vnd.ms-powerpoint' || fileType.includes('presentationml')) return 'presentation';
  if (fileType === 'text/csv') return 'csv';
  if (fileType === 'text/plain') return 'text';
  if (fileType === 'application/json' || fileType === 'text/json') return 'json';
  if (fileType.includes('html')) return 'html';
  if (fileType.includes('javascript') || fileType.includes('typescript')) return 'code';
  if (fileType.includes('zip') || fileType.includes('compressed') || fileType.includes('archive')) return 'archive';
  return 'other';
};
